<template>
  <EInstantAnswer
    class="flight-booking"
    :sources="sources"
    data-test-id="flight-booking"
    :feedback-link="{
      ticketSubject: 'Search - Flight Booking',
      widgetType: 'FlightBooking_rich'
    }"
    :sr-title="$t('common.search.widgets.flightbooking.title')"
  >
    <p>
      You need to disable CORS on your browser to test this.
    </p>
    <p v-if="error" class="flight-booking__error">
      {{ error }}
    </p>
    <form
      class="flight-booking__form"
      method="GET"
      :action="submitUrl"
      @submit="submit"
      @formdata="formdata"
    >
      <div
        v-on-clickaway="closeSuggestions"
        class="flight-booking-form__dropdown-field"
      >
        <EField
          v-model="origin"
          type="text"
          placeholder="Type to search"
          name="origin"
          label="From"
          force-stacked
          autocomplete="off"
          @focus="onOriginFocus"
          @blur="onOriginBlur"
          @input="searchOrigin"
        />
        <EDropdown :visible="showOriginDropdown && originSuggestions.length > 0">
          <EList role="none">
            <SuggestionItem
              v-for="suggestion in originSuggestions"
              :key="suggestion.entityId"
              v-bind="suggestion"
              @click="onSelectOrigin(suggestion)"
            />
          </EList>
        </EDropdown>
      </div>
      <div
        v-on-clickaway="closeSuggestions"
        class="flight-booking-form__dropdown-field"
      >
        <EField
          v-model="destination"
          autocomplete="off"
          type="text"
          name="destination"
          placeholder="Type to search"
          label="To"
          force-stacked
          @focus="onDestinationFocus"
          @blur="onDestinationBlur"
          @input="searchDestination"
        />
        <EDropdown :visible="showDestinationDropdown && destinationSuggestions.length > 0">
          <EList role="none">
            <SuggestionItem
              v-for="suggestion in destinationSuggestions"
              :key="suggestion.entityId"
              v-bind="suggestion"
              @click="onSelectDestination(suggestion)"
            />
          </EList>
        </EDropdown>
      </div>
      <EField
        v-model="numAdults"
        type="number"
        name="adultsv2"
        label="Adults"
        force-stacked
      />
      <EField
        v-model="numChildren"
        type="number"
        name="childrenv2"
        label="Children"
        force-stacked
      />
      <EField
        v-model="outboundDate"
        type="date"
        name="outboundDate"
        label="Departure date"
        force-stacked
      />
      <EField
        v-model="inboundDate"
        type="date"
        name="inboundDate"
        label="Return date"
        force-stacked
      />
      <EButton
        type="submit"
        variant="outline"
        class="flight-booking__submit"
        data-test-id="searchFlights"
      >
        Search Flights
      </EButton>
    </form>
  </EInstantAnswer>
</template>

<script>
import debounce from 'lodash/debounce';
import { mixin as clickaway } from 'vue-clickaway';

import { trackImpression, PAGE_SERP, TYPE_RICH } from '~~/search/common/js/client/analytics/rich-content';
import EInstantAnswer from '~/components/instant-answers/base';
import EField from '~~/common/vue/components/field';
import EButton from '~~/common/vue/components/button';
import EDropdown from '~~/common/vue/components/dropdown';
import EList from '~~/common/vue/components/list';
import SuggestionItem from './suggestionItem';
import { requestSuggestions, CABIN_CLASS_ECONOMY, getReferralsUrl } from './skyscanner';

export const TRACK_ID = 'flightBooking';
const AUTOSUGGEST_DEBOUNCE_DELAY = 250;

export default {
  name: 'EFlightBooking',
  components: {
    EInstantAnswer,
    EField,
    EButton,
    EDropdown,
    EList,
    SuggestionItem,
  },
  mixins: [clickaway],
  props: {
  },
  data() {
    return {
      origin: '',
      selectedOrigin: null,
      originSuggestions: [],
      showOriginDropdown: false,
      destination: '',
      selectedDestination: null,
      destinationSuggestions: [],
      showDestinationDropdown: false,
      numAdults: '1',
      numChildren: '0',
      outboundDate: '', // TODO: needs validation (valid date range and format YYYY-MM-DD)
      inboundDate: '', // TODO: needs validation (valid date range and format YYYY-MM-DD)
      cabinClass: CABIN_CLASS_ECONOMY, // we don't support other values atm
      isReturn: true, // TODO: needs input for this value
      error: '',
      submitUrl: '',
    };
  },
  computed: {
    sources() {
      return [
        {
          title: 'Skyscanner',
          url: 'https://www.skyscanner.net/',
        },
      ];
    },
  },
  mounted() {
    trackImpression({
      name: TRACK_ID,
      page: PAGE_SERP,
      type: TYPE_RICH,
      once: false,
    });
    this.debouncedOriginSuggestions = debounce(
      async () => {
        const data = await requestSuggestions(this.origin);
        this.originSuggestions = data.places;
      },
      AUTOSUGGEST_DEBOUNCE_DELAY,
    );
    this.debouncedDestinationSuggestions = debounce(
      async () => {
        const data = await requestSuggestions(this.destination);
        this.destinationSuggestions = data.places;
      },
      AUTOSUGGEST_DEBOUNCE_DELAY,
    );
  },
  methods: {
    closeSuggestions() {
      this.showOriginDropdown = false;
      this.showDestinationDropdown = false;
    },
    searchOrigin() {
      if (this.origin.length > 1) {
        this.showOriginDropdown = true;
        this.debouncedOriginSuggestions();
      }
    },
    searchDestination() {
      if (this.destination.length > 1) {
        this.showDestinationDropdown = true;
        this.debouncedDestinationSuggestions();
      }
    },
    onOriginFocus() {
      // The focus handler allows serching for a new location
      this.originSuggestions = [];
      this.origin = '';
    },
    onDestinationFocus() {
      this.destinationSuggestions = [];
      this.destination = '';
    },
    onOriginBlur() {
      // The blur handler allows preserving a previous search
      if (this.selectedOrigin) {
        [this.origin] = this.selectedOrigin.hierarchy.split(/[,|]/g);
      } else {
        this.origin = '';
      }
    },
    onDestinationBlur() {
      if (this.selectedDestination) {
        [this.destination] = this.selectedDestination.hierarchy.split(/[,|]/g);
      } else {
        this.destination = '';
      }
    },
    onSelectOrigin(originLocation) {
      this.selectedOrigin = originLocation;
      [this.origin] = originLocation.hierarchy.split(/[,|]/g);
    },
    onSelectDestination(destinationLocation) {
      this.selectedDestination = destinationLocation;
      [this.destination] = destinationLocation.hierarchy.split(/[,|]/g);
    },
    /**
     * Validates the data and selects the appropriate
     * target URL based on the provided data.
     *
     * @param {SubmitEvent} event
     */
    submit(event) {
      if (
        !this.cabinClass ||
        !this.adults ||
        !this.outboundDate ||
        !this.inboundDate) {
        if (this.selectedOrigin && this.selectedDestination) {
          // Calendar Month View
          this.submitUrl = getReferralsUrl('monthView');
        } else if (this.selectedOrigin) {
          // Browse View
          this.submitUrl = getReferralsUrl('browseView');
        } else if (this.selectedDestination) {
          // Cheap flights to
          this.submitUrl = getReferralsUrl('cheapFlightsTo');
        } else {
          this.error = 'At least origin or destination is required';
          event.preventDefault();
          return false;
        }
      } else if (
        this.selectedOrigin &&
        this.selectedDestination &&
        this.outboundDate &&
        this.inboundDate &&
        this.numAdults &&
        this.cabinClass
      ) {
        // Day View
        this.submitUrl = getReferralsUrl('dayView');
      } else {
        this.error = 'At least origin or destination is required';
        event.preventDefault();
        return false;
      }

      // Remove null data from form
      return false;
    },
    /**
     * Before submitting the form data, we need to remove any null or empty values.
     * The API doesn't accept null values.
     * Some values also need some-preprocessing.
     *
     * @param {FormDataEvent} event
     */
    formdata({ formData }) {
      for (const key of formData.keys()) {
        if (!formData.get(key)) {
          formData.delete(key);
        }
      }
      if (formData.get('origin')) {
        formData.set('origin', this.selectedOrigin.iataCode.toLowerCase());
      }
      if (formData.get('destination')) {
        formData.set('destination', this.selectedDestination.iataCode.toLowerCase());
      }
    },
  },
};
</script>3

<style lang="scss" scoped>
.flight-booking__form {
  display: grid;
  grid-template-columns: 2fr 2fr 1fr 1fr;
  gap: $space-s;
}

.field + .field {
  margin-top: 0;
}

.flight-booking__submit {
  grid-column: 3 / 5;
  place-self: self-end;
}

.flight-booking-form__dropdown-field {
  position: relative;
}

.dropdown {
  min-width: 300px;
  max-height: 390px;
  padding: $space-m 0;
  overflow: auto;
}

.flight-booking__error {
  color: var(--color-error);
}
</style>
